
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  UserBaseInfoInterface,
  UserRepoInterface, UserResponseInterface,
} from '@/services/Users/Interfaces';
import {
  StatisticsRepositoryInterface, StatisticsResponseInterface,
} from '@/services/QA/statistics/interfaces';
import { CommentInterface, DossierCommentInterface } from '@/services/QA/comment/interfaces';
import FeedbackRepositoryInterface, {
  FeedbackInterface,
} from '@/services/QA/feedback/interfaces';
import { DataTableHeader } from 'vuetify';
import DeleteCommentDialog from '@/components/dialogs/qa/comment/DeleteCommentDialog.vue';
import EditCommentDialog from '@/components/dialogs/qa/comment/EditCommentDialog.vue';
import { UserHasScopes } from '@/views/middleware/Route/Auth';
import { Roles } from '@/services/Roles';
import { ProfileInterface, ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import TagRepositoryInterface, { TagInterface } from '@/services/QA/tag/interfaces';
import FeedbackCommentDialog from '@/components/dialogs/qa/feedback/FeedbackCommentDialog.vue';
import { TicketInterface } from '@/services/QA/ticket/interfaces';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    DeleteCommentDialog,
    EditCommentDialog,
    FeedbackCommentDialog,
  },
  inject: [
    'userRepository',
    'statisticRepository',
    'feedbackRepository',
    'profileRepository',
    'tagRepository',
  ],
})
export default class Dossier extends Vue {
  private userRepository!: UserRepoInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private statisticRepository!: StatisticsRepositoryInterface;
  private feedbackRepository!: FeedbackRepositoryInterface;
  private tagRepository!: TagRepositoryInterface;

  private user!: UserResponseInterface;
  private profile: ProfileInterface;
  private statistic: StatisticsResponseInterface;
  private loading: boolean = true;
  private comments: DossierCommentInterface[] = [];
  private feedbacks: FeedbackInterface[] = [];
  private comment: string = '';

  private usersMap: { [userId: string]: UserBaseInfoInterface } = {};
  private tagsMap: { [tagId: number]: TagInterface } = {};

  private feedbackHeaders: DataTableHeader[] = [
    { text: 'Status', value: 'status' },
    { text: 'Type', value: 'type' },
    { text: 'Resource ID', value: 'resource' },
    { text: 'Date & Time', value: 'timestamp' },
    { text: 'Tags', value: 'tags' },
    { text: 'Feedback From', value: 'feedbackFromUserId' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  private commentIdForDialog: number = 0;
  private deleteCommentDialog: string | false = false;
  private commentIdForEditDialog: number = 0;
  private commentContentForDialog: string = '';
  private editCommentDialog: string | false = false;
  private userHasScopes: object = UserHasScopes;
  private userRoles: object = Roles;
  private feedbacksPerPage: number = 5;

  private feedbackCommentDialog: string | false = false;
  private titleForFeedbackCommentDialog: string = '';
  private commentsForFeedbackCommentDialog: CommentInterface[] = [];


  public async created(): Promise<void> {
    this.user = await this.userRepository.getUser(this.$route.params.userId);
    this.profile = await this.profileRepository.getOneByUserId(this.$route.params.userId);
    this.statistic = await this.statisticRepository.getAgentStatistic(this.$route.params.userId);
    this.comments = (await this.statisticRepository.getAgentComments(this.$route.params.userId)) || [];
    await this.getUserFeedbacks();

    await this.fetchTags();
    await this.getUsersData();

    this.loading = false;
  }

  private async fetchTags(): Promise<void> {
    const tags = await this.tagRepository.getActiveTags();
    tags.forEach((tag) => {
      this.tagsMap[tag.id] = tag;
    });
  }

  private async addComment(): Promise<void> {
    try {
      const commentDTO = {
        agentId: this.$route.params.userId,
        authorId: this.$store.getters.id,
        content: this.comment,
        timestamp: Math.floor(Date.now() / 1000),
      } as CommentInterface;
      const comment = (await this.statisticRepository.createComment(this.$route.params.userId, commentDTO));

      if (!this.usersMap[comment.comment.authorId]) {
        const user = await this.userRepository.getUsersByIds([comment.comment.authorId]);
        this.usersMap[user[0].id] = user[0];
      }
      this.comments.push(comment);
      this.comment = '';
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message);
    }
  }

  private async getUsersData(): Promise<void> {
    const ids: string[] = [];
    this.comments.forEach((comment) => {
      if (ids.indexOf(comment.comment.authorId) === -1) {
        ids.push(comment.comment.authorId);
      }

      if (ids.indexOf(comment.comment.agentId) === -1) {
        ids.push(comment.comment.agentId);
      }
    });

    this.feedbacks.forEach((feedback) => {
      if (ids.indexOf(feedback.feedbackFromUserId) === -1) {
        ids.push(feedback.feedbackFromUserId);
      }

      if (ids.indexOf(feedback.feedbackToUserId) === -1) {
        ids.push(feedback.feedbackToUserId);
      }
    });

    const users = await this.userRepository.getUsersByIds(ids);
    users.forEach((user) => {
      this.usersMap[user.id] = user;
    });
  }

  private async prepareCommentForEdit(commentId: number): Promise<void> {
    const commentIndex = this.comments.findIndex((dossierComment) => dossierComment.comment.id === commentId);
    if (commentIndex !== -1) {
      this.commentIdForEditDialog = this.comments[commentIndex].comment.id;
      this.commentContentForDialog = this.comments[commentIndex].comment.content;
      this.editCommentDialog = 'edit-comment-dialog';
    }
  }

  private async editComment(commentId: number, content: string): Promise<void> {
    const commentIndex = this.comments.findIndex((dossierComment) => dossierComment.comment.id === commentId);
    if (commentIndex !== -1) {
      this.comments[commentIndex].comment.content = content;
      this.comments[commentIndex].comment.updatedAt = Math.floor(Date.now() / 1000);
    }
  }

  private async deleteComment(commentId: number): Promise<void> {
    this.commentIdForDialog = commentId;
    this.deleteCommentDialog = 'delete-comment-dialog';
  }

  private removeComment(commentId: number): void {
    const commentIndex = this.comments.findIndex((dossierComment) => dossierComment.comment.id === commentId);

    if (commentIndex !== -1) {
      delete this.comments[commentIndex];
      this.comments = this.comments.filter((comment) => !!comment);
    }

    this.$forceUpdate();
  }

  private getCurrentFeedbacksPage(lastElement: number): number {
    return Math.ceil(lastElement / this.feedbacksPerPage);
  }

  private async getUserFeedbacks(): Promise<void> {
    const feedbacks = await this.feedbackRepository.getUserFeedbacks(this.$route.params.userId);
    this.feedbacks = feedbacks.filter((feedback) => !!feedback.chatFeedback || !!feedback.ticketFeedback)
      .sort((feedbackA, feedbackB) => feedbackB.timestamp - feedbackA.timestamp);
  }

  private showFeedbackCommentsDialog(feedback: FeedbackInterface): void {
    this.feedbackCommentDialog = 'feedback-comment-dialog';
    if (feedback.chatFeedback) {
      this.titleForFeedbackCommentDialog = `Chat Feedback [${feedback.chatFeedback.chatId}]`;
      this.commentsForFeedbackCommentDialog = feedback.chatFeedback.chat.comments
        .map((ticketComment) => ticketComment.comment);
      return;
    }

    this.titleForFeedbackCommentDialog = `Ticket Feedback [${feedback.ticketFeedback.ticketId}]`;
    this.commentsForFeedbackCommentDialog = feedback.ticketFeedback.ticket.comments
      .map((ticketComment) => ticketComment.comment);
  }
}
